import React from 'react';
import styled from 'styled-components';
import { IconButton, Tooltip } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import { CampaignChannel, CampaignStatus } from '../../models';
import { ComboField, TextInputField } from '../../components/Forms/fields';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getCampaignStatusTranslationKey } from './campaignsUtils';
import AppButton from '../../components/AppButton';

const Toolbar = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) repeat(2, minmax(160px, 1fr)) auto;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-M);
    align-items: end;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const ToolbarActions = styled.div`
    display: flex;
    align-items: flex-end;
    gap: var(--spacer-XS);
`;

const SettingsIconButton = styled(IconButton)`
    margin-bottom: var(--spacer-XXS) !important;
`;

export interface CampaignsToolbarProps {
    searchValue: string;
    statusValue: string;
    channelValue: string;
    onSearchChange: (value: string) => void;
    onStatusChange: (value: string) => void;
    onChannelChange: (value: string) => void;
    onCreate: () => void;
    creating: boolean;
    onSettingsClick: () => void;
}

export const CampaignsToolbar: React.FC<CampaignsToolbarProps> = ({
    searchValue,
    statusValue,
    channelValue,
    onSearchChange,
    onStatusChange,
    onChannelChange,
    onCreate,
    creating,
    onSettingsClick,
}) => {
    const t = useTranslations();

    const statusOptions = [
        { value: '', label: t(TK.allStatuses) },
        { value: String(CampaignStatus.Draft), label: t(getCampaignStatusTranslationKey(CampaignStatus.Draft)) },
        { value: String(CampaignStatus.Ready), label: t(getCampaignStatusTranslationKey(CampaignStatus.Ready)) },
        { value: String(CampaignStatus.AwaitingPayment), label: t(getCampaignStatusTranslationKey(CampaignStatus.AwaitingPayment)) },
        { value: String(CampaignStatus.Paid), label: t(getCampaignStatusTranslationKey(CampaignStatus.Paid)) },
        { value: String(CampaignStatus.Scheduled), label: t(getCampaignStatusTranslationKey(CampaignStatus.Scheduled)) },
        { value: String(CampaignStatus.Sending), label: t(getCampaignStatusTranslationKey(CampaignStatus.Sending)) },
        { value: String(CampaignStatus.Sent), label: t(getCampaignStatusTranslationKey(CampaignStatus.Sent)) },
        { value: String(CampaignStatus.PartiallyFailed), label: t(getCampaignStatusTranslationKey(CampaignStatus.PartiallyFailed)) },
        { value: String(CampaignStatus.Failed), label: t(getCampaignStatusTranslationKey(CampaignStatus.Failed)) },
        { value: String(CampaignStatus.Archived), label: t(getCampaignStatusTranslationKey(CampaignStatus.Archived)) },
    ];

    const channelOptions = [
        { value: '', label: t(TK.allChannels) },
        { value: String(CampaignChannel.Email), label: t(TK.email) },
        { value: String(CampaignChannel.WhatsApp), label: t(TK.whatsApp) },
        { value: String(CampaignChannel.SMS), label: t(TK.sms) },
    ];

    return (
        <Toolbar>
            <TextInputField
                id="campaigns-search"
                label={t(TK.search)}
                value={searchValue}
                onChange={onSearchChange}
            />
            <ComboField
                id="campaigns-status"
                label={t(TK.status)}
                value={statusValue}
                options={statusOptions.map(o => ({ value: o.value, label: o.label }))}
                onChange={v => onStatusChange(v ?? '')}
            />
            <ComboField
                id="campaigns-channel"
                label={t(TK.channel)}
                value={channelValue}
                options={channelOptions.map(o => ({ value: o.value, label: o.label }))}
                onChange={v => onChannelChange(v ?? '')}
            />
            <ToolbarActions>
                <Tooltip title={t(TK.whatsappTemplates)}>
                    <SettingsIconButton size="small" onClick={onSettingsClick}>
                        <SettingsIcon />
                    </SettingsIconButton>
                </Tooltip>
                <AppButton color="success" onClick={onCreate} disabled={creating}>
                    {t(TK.newCampaign)}
                </AppButton>
            </ToolbarActions>
        </Toolbar>
    );
}
